import React from 'react';
import type { PreprocessingResponse } from '../services/api';

interface PreprocessingResultProps {
  result: PreprocessingResponse;
}

// The text as the model would see it. v5 shows the masked lines that reach the
// encoder; v4 shows the cleaned text each head is given. The UI sends a single
// description field, so the per-field breakdown only appears when it differs from
// the cargo-level lines.
const PreprocessingResult: React.FC<PreprocessingResultProps> = ({ result }) => {
  const isV5 = result.lines !== null;
  const cargoLines = result.lines ?? [];
  const fieldLinesDiffer = result.fields.some(
    (field) => field.lines !== null && field.lines.join('\n') !== cargoLines.join('\n')
  );

  return (
    <div className="card result-card">
      <div className="result-header">
        <span className="h5">Model input</span>
        <span className="tag">preprocessing {result.modelVersion}</span>
      </div>

      {isV5 ? (
        <>
          {cargoLines.length === 0 ? (
            <div className="result-note">
              Nothing survived cleaning and masking; a dg v5 score would be the customer prior.
            </div>
          ) : (
            <pre className="preprocessed-lines">{cargoLines.join('\n')}</pre>
          )}

          {result.unRuleDg && (
            <div className="verdict-notes">
              <div>Declared UN number with no exempting special provision: dg v5 would force the verdict to dangerous.</div>
            </div>
          )}

          {fieldLinesDiffer && (
            <div className="preprocessed-fields">
              {result.fields.map((field) => (
                <div key={field.field} className="preprocessed-field">
                  <small className="preprocessed-field-name">{field.field}, masked alone</small>
                  <pre className="preprocessed-lines">{(field.lines ?? []).join('\n') || '(nothing)'}</pre>
                </div>
              ))}
            </div>
          )}
        </>
      ) : (
        <div className="preprocessed-fields">
          {result.fields.map((field) => (
            <div key={field.field} className="preprocessed-field">
              <small className="preprocessed-field-name">{field.field}, for the DG model</small>
              <pre className="preprocessed-lines">{field.dgText || '(nothing)'}</pre>
              {field.unText !== field.dgText && (
                <>
                  <small className="preprocessed-field-name">{field.field}, for the UN model</small>
                  <pre className="preprocessed-lines">{field.unText || '(nothing)'}</pre>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PreprocessingResult;
